import { assert } from '@ember/debug';
import { doesNotExist, exists, hasCorrectType, isActive, isInactive } from './helpers';
import { isEmpty } from '@ember/utils';

/**
 * Validates that the provided argument has no value (is `undefined` or `null`).
 * Supports three validation modes:
 * 1. Legacy validation (no forbiddingArgValue): Always validates
 * 2. Existence-based (forbiddingArgValue, no when): Validates when forbidding arg is active
 * 3. Value-based (forbiddingArgValue + when): Validates when forbidding arg matches any value in 'when' array
 *
 * Note: For boolean forbidding arguments, `false` is treated as inactive (not triggering the forbidden check).
 *
 * @param {object} validationData - An object containing:
 *   - `argument`: The forbidden argument value to check.
 *   - `forbiddingArgValue`: Optional - the value of the argument that forbids this one.
 *   - `when`: Optional - array of values that the forbiddingArgValue must match to trigger validation.
 *   - `message`: The error message to display if validation fails.
 */
export function forbiddenAssertion(validationData) {
  let { argument, forbiddingArgValue, message, when } = validationData;
  let isLegacyValidation = !('forbiddingArgValue' in validationData);
  let hasForbiddingArg = !isLegacyValidation && isActive(forbiddingArgValue);
  let hasConditionalValues = 'when' in validationData && Array.isArray(when) && when.length > 0;
  let shouldForbid = hasConditionalValues && when.includes(forbiddingArgValue);
  let hasForbiddingArgWithoutCondition = hasForbiddingArg && !hasConditionalValues;
  let shouldValidate = isLegacyValidation || shouldForbid || hasForbiddingArgWithoutCondition;

  if (shouldValidate) {
    assert(message, isInactive(argument));
  }
}

/**
 * optionalAssertion validates that the argument either has the expected data type
 * or has not been provided
 * if the argument fails this assertion, the provided message is thrown as an Error
 * @param {object} param0 with attributes message, type, argument
 */
export function optionalAssertion({ argument, message, type }) {
  assert(message, hasCorrectType(argument, type) || doesNotExist(argument));
}

/**
 * requiredAssertion validates that the provided argument exists and has the expected data type
 * if the argument fails this assertion, the provided message is thrown as an Error
 * @param {object} param0 with attributes argument, message, type
 */
export function requiredAssertion({ argument, message, type }) {
  assert(message, hasCorrectType(argument, type) && exists(argument));
}

/**
 * Validates that a required dependency exists when the requiring argument is provided.
 * Only validates when the requiring argument has a value.
 *
 * Note: For boolean requiring arguments, `false` is treated as inactive (not triggering the requires check).
 *
 * @param {object} validationData - An object containing:
 *   - `requiringArgValue`: The value of the argument that requires another argument.
 *   - `requiredArgValue`: The value of the required dependency argument.
 *   - `message`: The error message to display if validation fails.
 */
export function requiresAssertion(validationData) {
  let { requiringArgValue, requiredArgValue, message } = validationData;
  let hasRequiringArg = isActive(requiringArgValue);

  if (hasRequiringArg) {
    assert(message, isActive(requiredArgValue));
  }
}

/**
 * optionalConfigOption validates that the provided configuration option is either
 * one of the expected values or has not been provided
 * if the config option fails this assertion, the provided message is thrown as an Error
 * @param {object} param0 with attributes argument, options, name, component, routeInfo, debugHint
 */
export function optionalConfigOption({
  argument,
  component,
  name,
  options,
  routeInfo = '',
  debugHint = '',
}) {
  assert(
    `@${name} argument of ${component} must be one of ${options.join(', ')} or empty${routeInfo}${debugHint}`,
    options.includes(argument) || doesNotExist(argument)
  );
}

/**
 * requiredConfigOption validates that the provided argument is one of the values in options
 * if the argument fails this assertion, the provided message is thrown as an Error
 * @param {object} param0 with attributes argument, component, name, options, routeInfo, debugHint
 */
export function requiredConfigOption({
  argument,
  component,
  name,
  options,
  routeInfo = '',
  debugHint = '',
}) {
  assert(
    `@${name} argument of ${component} must be one of ${options.join(', ')}${routeInfo}${debugHint}`,
    options.includes(argument) && !isEmpty(argument)
  );
}

// Assertion configuration objects
export const configOptionAssertions = {
  forbidden: forbiddenAssertion,
  optional: optionalConfigOption,
  required: requiredConfigOption,
  requires: requiresAssertion,
};

export const defaultAssertions = {
  forbidden: forbiddenAssertion,
  optional: optionalAssertion,
  required: requiredAssertion,
  requires: requiresAssertion,
};

export default {
  forbiddenAssertion,
  optionalAssertion,
  requiredAssertion,
  optionalConfigOption,
  requiredConfigOption,
  requiresAssertion,
  configOptionAssertions,
  defaultAssertions,
};
